import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchAddress } from '@/lib/viacep';
import { ESTADOS_BRASILEIROS } from '@/lib/estados';
import { Loader2, Calendar } from 'lucide-react';

interface Customer {
  id: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  notes: string | null;
}

interface ClienteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer | null;
  onSaved: () => void;
}

export function ClienteModal({ open, onOpenChange, customer, onSaved }: ClienteModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    tax_id: '',
    birth_date: '',
    whatsapp: '',
    sms_phone: '',
    email: '',
    // Cobrança fields (placeholders for future use)
    valor: '',
    juros: '',
    multa: '',
    dia_pagamento: '',
    ultimo_mes: '',
    regua_id: '',
    // Descrição
    notes: '',
    // Endereço
    postal_code: '',
    address: '',
    numero: '',
    state: '',
    city: '',
    bairro: '',
    complemento: ''
  });
  const [error, setError] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const queryClient = useQueryClient();

  // Fetch collection rules for régua dropdown
  const { data: reguas = [] } = useQuery({
    queryKey: ['collection-rules-dropdown'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from('collection_rules')
        .select('id, name')
        .eq('company_id', profile.company_id)
        .order('name');

      if (error) return [];
      return data;
    }
  });

  useEffect(() => {
    if (open) {
      if (customer) {
        setFormData({
          name: customer.name || '',
          tax_id: customer.tax_id || '',
          birth_date: '',
          whatsapp: customer.phone || '',
          sms_phone: customer.phone || '',
          email: customer.email || '',
          valor: '',
          juros: '',
          multa: '',
          dia_pagamento: '',
          ultimo_mes: '',
          regua_id: '',
          notes: customer.notes || '',
          postal_code: customer.postal_code || '',
          address: customer.address || '',
          numero: '',
          state: customer.state || '',
          city: customer.city || '',
          bairro: '',
          complemento: ''
        });
      } else {
        setFormData({
          name: '',
          tax_id: '',
          birth_date: '',
          whatsapp: '',
          sms_phone: '',
          email: '',
          valor: '',
          juros: '',
          multa: '',
          dia_pagamento: '',
          ultimo_mes: '',
          regua_id: '',
          notes: '',
          postal_code: '',
          address: '',
          numero: '',
          state: '',
          city: '',
          bairro: '',
          complemento: ''
        });
      }
      setError('');
    }
  }, [open, customer]);

  const handleCepBlur = async () => {
    const cep = formData.postal_code.replace(/\D/g, '');
    if (cep.length !== 8) return;

    setCepLoading(true);
    try {
      const addressData = await fetchAddress(cep);
      if (addressData) {
        setFormData(prev => ({
          ...prev,
          address: addressData.logradouro || prev.address,
          city: addressData.localidade || prev.city,
          state: addressData.uf || prev.state,
          bairro: addressData.bairro || prev.bairro
        }));
      }
    } finally {
      setCepLoading(false);
    }
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!formData.name.trim() || !formData.tax_id.trim()) {
        throw new Error('Nome e Documento são obrigatórios');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Empresa não encontrada');

      // Build full address with numero and complemento
      let fullAddress = formData.address.trim();
      if (formData.numero) fullAddress += `, ${formData.numero}`;
      if (formData.complemento) fullAddress += ` - ${formData.complemento}`;

      const payload = {
        name: formData.name.trim(),
        tax_id: formData.tax_id.replace(/\D/g, ''),
        email: formData.email.trim() || null,
        phone: formData.whatsapp.replace(/\D/g, '') || null,
        address: fullAddress || null,
        city: formData.city.trim() || null,
        state: formData.state.trim().toUpperCase() || null,
        postal_code: formData.postal_code.replace(/\D/g, '') || null,
        notes: formData.notes.trim() || null,
        company_id: profile.company_id
      };

      if (customer) {
        const { error } = await supabase
          .from('customers')
          .update(payload)
          .eq('id', customer.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('customers')
          .insert(payload);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      onSaved();
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message);
    }
  });

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {customer ? 'Editar Cliente' : 'Novo Cliente'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Row 1: Nome, CPF/CNPJ, Data de Nascimento */}
          <div className="grid grid-cols-[1fr_180px_160px] gap-4">
            <div className="space-y-1">
              <Label htmlFor="name" className="text-xs text-muted-foreground">Nome</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Nome completo"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tax_id" className="text-xs text-muted-foreground">CPF ou CNPJ</Label>
              <Input
                id="tax_id"
                value={formData.tax_id}
                onChange={(e) => handleChange('tax_id', e.target.value)}
                placeholder="000.000.000-00"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="birth_date" className="text-xs text-muted-foreground">Data de Nascimento</Label>
              <div className="relative">
                <Input
                  id="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => handleChange('birth_date', e.target.value)}
                  className="pr-10"
                />
                <Calendar className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>

          {/* CONTATO Section */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Contato</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label htmlFor="whatsapp" className="text-xs text-muted-foreground">WhatsApp</Label>
                <Input
                  id="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => handleChange('whatsapp', e.target.value)}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sms_phone" className="text-xs text-muted-foreground">SMS</Label>
                <Input
                  id="sms_phone"
                  value={formData.sms_phone}
                  onChange={(e) => handleChange('sms_phone', e.target.value)}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="email" className="text-xs text-muted-foreground">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>
          </div>

          {/* COBRANÇA Section */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Cobrança</p>
            <div className="space-y-4">
              {/* Row 1: Valor, Juros, Multa */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="valor" className="text-xs text-muted-foreground">Valor</Label>
                  <Input
                    id="valor"
                    value={formData.valor}
                    onChange={(e) => handleChange('valor', e.target.value)}
                    placeholder="R$ 0,00"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="juros" className="text-xs text-[hsl(var(--accent-600))]">Juros</Label>
                  <div className="relative">
                    <Input
                      id="juros"
                      value={formData.juros}
                      onChange={(e) => handleChange('juros', e.target.value)}
                      placeholder=""
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="multa" className="text-xs text-muted-foreground">Multa</Label>
                  <div className="relative">
                    <Input
                      id="multa"
                      value={formData.multa}
                      onChange={(e) => handleChange('multa', e.target.value)}
                      placeholder=""
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                  </div>
                </div>
              </div>

              {/* Row 2: Dia de pagamento, Último mês, Régua */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="dia_pagamento" className="text-xs text-muted-foreground">Dia de pagamento</Label>
                  <Input
                    id="dia_pagamento"
                    type="number"
                    min="1"
                    max="31"
                    value={formData.dia_pagamento}
                    onChange={(e) => handleChange('dia_pagamento', e.target.value)}
                    placeholder="1"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ultimo_mes" className="text-xs text-muted-foreground">Último mês</Label>
                  <div className="relative">
                    <Input
                      id="ultimo_mes"
                      type="month"
                      value={formData.ultimo_mes}
                      onChange={(e) => handleChange('ultimo_mes', e.target.value)}
                      className="pr-10"
                    />
                    <Calendar className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  </div>
                  <span className="text-[10px] text-muted-foreground italic">Vazio para recorrente</span>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="regua_id" className="text-xs text-muted-foreground">Régua</Label>
                  <select
                    id="regua_id"
                    value={formData.regua_id}
                    onChange={(e) => handleChange('regua_id', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Escolha automática</option>
                    {reguas.map((regua) => (
                      <option key={regua.id} value={regua.id}>{regua.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* DESCRIÇÃO DA COBRANÇA Section */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Descrição da Cobrança</p>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Descrição ou observações sobre a cobrança..."
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* ENDEREÇO Section */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Endereço</p>
            <div className="space-y-4">
              {/* Row 1: CEP, Logradouro, Nº */}
              <div className="grid grid-cols-[140px_1fr_80px] gap-4">
                <div className="space-y-1 relative">
                  <Label htmlFor="postal_code" className="text-xs text-muted-foreground">CEP</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    onBlur={handleCepBlur}
                    placeholder="00000-000"
                  />
                  {cepLoading && (
                    <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-8 text-muted-foreground" />
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="address" className="text-xs text-muted-foreground">Logradouro</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Rua, Av, etc."
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="numero" className="text-xs text-muted-foreground">Nº</Label>
                  <Input
                    id="numero"
                    value={formData.numero}
                    onChange={(e) => handleChange('numero', e.target.value)}
                    placeholder="123"
                  />
                </div>
              </div>

              {/* Row 2: Estado, Cidade, Bairro */}
              <div className="grid grid-cols-[160px_1fr_1fr] gap-4">
                <div className="space-y-1">
                  <Label htmlFor="state" className="text-xs text-muted-foreground">Estado</Label>
                  <select
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Selecione</option>
                    {ESTADOS_BRASILEIROS.map(estado => (
                      <option key={estado.sigla} value={estado.sigla}>
                        {estado.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="city" className="text-xs text-muted-foreground">Cidade</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Cidade"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bairro" className="text-xs text-muted-foreground">Bairro</Label>
                  <Input
                    id="bairro"
                    value={formData.bairro}
                    onChange={(e) => handleChange('bairro', e.target.value)}
                    placeholder="Bairro"
                  />
                </div>
              </div>

              {/* Row 3: Complemento */}
              <div className="space-y-1">
                <Input
                  id="complemento"
                  value={formData.complemento}
                  onChange={(e) => handleChange('complemento', e.target.value)}
                  placeholder="Complemento"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
